import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "../style/installmentpage.css";
import { PageHeader, SearchInput, EmptyState } from "../components/admin/AdminUI";

const formatRupiah = (n) =>
  typeof n === "number" ? `Rp${n.toLocaleString("id-ID")}` : "-";

const statusBadge = (status) => {
  if (status === "awaiting_payment")
    return { bg: "#fff8e1", color: "#d97706", text: "Awaiting Payment" };
  if (status === "payment_failed")
    return { bg: "#fef2f2", color: "#dc2626", text: "Failed / Expired" };
  return { bg: "#f3f4f6", color: "#666", text: status };
};

const sourceBadge = (source) =>
  source === "church"
    ? { bg: "#fff0ee", color: "#fb3104", text: "CEA To Church" }
    : { bg: "#eef1ff", color: "#3a45c9", text: "Individual" };

// Halaman admin "Pending Payments": SEMUA pendaftar yang link pembayarannya
// belum lunas — awaiting_payment atau payment_failed. Mencakup pendaftar
// individu (tabel Approval) DAN payer CEA To Church (PIC/Student/Other, tagihan
// awal). Admin bisa Resend Link / Edit Email tanpa pendaftar mengisi form ulang.
const PendingPaymentsPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const [errorMe, setErrorMe] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all | awaiting_payment | payment_failed
  const [busyKey, setBusyKey] = useState(null);

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch {
      setErrorMe(true);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [indRes, churchRes] = await Promise.all([
        axios.get("/pending-payments").catch(() => ({ data: [] })),
        axios.get("/church-pending-payments").catch(() => ({ data: [] })),
      ]);
      const individual = (indRes.data || []).map((r) => ({
        key: `ind-${r.email}`,
        source: "individual",
        name: r.name,
        email: r.email,
        context: r.program || "-",
        amount: r.finalPrice,
        paymentStatus: r.paymentStatus,
      }));
      const church = (churchRes.data || []).map((r) => ({
        key: `chu-${r.allocationId}`,
        source: "church",
        allocationId: r.allocationId,
        name: r.payerName,
        email: r.payerEmail,
        context: `${r.churchName} · ${r.payerType}${r.percent != null ? ` ${r.percent}%` : ""}`,
        amount: r.amount,
        paymentStatus: r.paymentStatus,
      }));
      setRows([...individual, ...church]);
    } catch {
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setLocationAdmin({ pathname: "/pendingpayments" });
    getMe();
    fetchData();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account!",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  const swalBase = {
    background: "white",
    color: "black",
    backdrop: `rgba(131, 131, 131, 0.5)`,
    confirmButtonColor: "#0004ff",
    confirmButtonText: "OK",
  };

  const resendUrl = (row) =>
    row.source === "church"
      ? `/church-allocation/${row.allocationId}/resend`
      : `/approval/${row.email}/resend`;

  const editEmailUrl = (row) =>
    row.source === "church"
      ? `/church-allocation/${row.allocationId}/email`
      : `/approval/${row.email}/email`;

  const handleResend = async (row) => {
    const confirm = await Swal.fire({
      title: "Resend Payment Link",
      text: `Send a fresh payment link to ${row.email}? The old link (if any) will be cancelled.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Resend",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
    });
    if (!confirm.isConfirmed) return;
    setBusyKey(row.key);
    try {
      const res = await axios.post(resendUrl(row));
      await Swal.fire({ ...swalBase, text: res.data.msg, icon: "success" });
      fetchData();
    } catch (err) {
      await Swal.fire({
        ...swalBase,
        text: err.response?.data?.msg || "Failed to resend the payment link.",
        icon: "error",
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleEditEmail = async (row) => {
    const { value: newEmail } = await Swal.fire({
      title: "Edit Registrant Email",
      input: "email",
      inputLabel: "Correct email address",
      inputValue: row.email,
      inputPlaceholder: "correct@email.com",
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Save",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      inputValidator: (v) => (!v ? "Please enter an email address" : undefined),
    });
    if (!newEmail || newEmail.trim().toLowerCase() === (row.email || "").toLowerCase()) return;
    setBusyKey(row.key);
    try {
      const res = await axios.patch(editEmailUrl(row), { newEmail: newEmail.trim() });
      await Swal.fire({ ...swalBase, text: res.data.msg, icon: "success" });
      fetchData();
    } catch (err) {
      await Swal.fire({
        ...swalBase,
        text: err.response?.data?.msg || "Failed to update the email.",
        icon: "error",
      });
    } finally {
      setBusyKey(null);
    }
  };

  const filtered = rows
    .filter((r) => filterStatus === "all" || r.paymentStatus === filterStatus)
    .filter((r) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        (r.name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.context || "").toLowerCase().includes(q)
      );
    });

  const counts = {
    all: rows.length,
    awaiting_payment: rows.filter((r) => r.paymentStatus === "awaiting_payment").length,
    payment_failed: rows.filter((r) => r.paymentStatus === "payment_failed").length,
  };

  const TABS = [
    { id: "all", label: "All" },
    { id: "awaiting_payment", label: "Awaiting Payment" },
    { id: "payment_failed", label: "Failed / Expired" },
  ];

  return (
    <div className="adm-main">
      <PageHeader
        title="Pending Payments"
        subtitle="Registrants whose payment link isn't paid yet — individual registrants and CEA To Church payers. Resend the link or fix a wrong email without asking them to fill the form again."
      />

      <div className="adm-toolbar">
        <div className="adm-tabs" style={{ margin: 0 }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`adm-tab${filterStatus === t.id ? " adm-tab--active" : ""}`}
              onClick={() => setFilterStatus(t.id)}
            >
              {t.label}
              <span className="adm-tab-count">{counts[t.id]}</span>
            </button>
          ))}
        </div>
        <div className="adm-toolbar-spacer" />
        <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, or church" />
      </div>

      <div className="adm-card adm-card--flush">
        {isLoading ? (
          <EmptyState icon="bi-hourglass" title="Loading pending payments…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="bi-check2-circle" title="No pending payments" text="Every payment link has been paid." />
        ) : (
          <div className="adm-table-wrap adm-table-wrap--scroll">
            <table className="adm-table" style={{ minWidth: "860px" }}>
              <thead>
                <tr>
                  <th>Registrant</th>
                  <th>Source</th>
                  <th>Program / Church</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th className="adm-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const badge = statusBadge(r.paymentStatus);
                  const src = sourceBadge(r.source);
                  const busy = busyKey === r.key;
                  return (
                    <tr key={r.key}>
                      <td>
                        <div className="adm-cell-title">{r.name}</div>
                        <div className="adm-cell-meta" style={{ wordBreak: "break-all" }}>{r.email}</div>
                      </td>
                      <td>
                        <span className="adm-badge" style={{ backgroundColor: src.bg, color: src.color }}>{src.text}</span>
                      </td>
                      <td style={{ color: "var(--adm-text-2)" }}>{r.context}</td>
                      <td>
                        <span className="adm-status" style={{ backgroundColor: badge.bg, color: badge.color }}>{badge.text}</span>
                      </td>
                      <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{formatRupiah(r.amount)}</td>
                      <td className="adm-col-actions">
                        <div className="adm-actions">
                          <button
                            type="button"
                            className="adm-icon-btn"
                            title="Edit email"
                            aria-label="Edit email"
                            disabled={busy}
                            onClick={() => handleEditEmail(r)}
                          >
                            <i className="bi bi-envelope"></i>
                          </button>
                          <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" disabled={busy} onClick={() => handleResend(r)}>
                            {busy ? "Sending..." : "Resend Link"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingPaymentsPage;
